﻿// ---------------------------------------------------------------------------
// strand_renderer.ts  —  Field Solver Visualization
//
// Renders the complex-potential field state on a canvas:
//   - Strand glow colored by Re(V) interpolated between nodes (gold→purple)
//   - Strand brightness from |I| (current magnitude)
//   - Animated flow dashes advancing along strands in current direction
//   - Loop shimmer / induction fill at loop centers
//   - Astral frame overlay for rectangular grand loops
//   - Source (gold) and co-source (purple) node markers
//
// No motes — purely field-based visualization.
// ---------------------------------------------------------------------------

import type { StrandGraph, Strand, Loop, StrandNode } from '../../src/core/strand_graph.js';
import type { FluxState } from '../../src/core/flux_engine.js';

// ── Colors ───────────────────────────────────────────────────────────────────

// Gold (Re(V) > 0)
const GOLD_CORE  = '#f0c850';
const GOLD_GLOW  = '#fff4d0';

// Purple (Re(V) < 0)
const PURP_CORE  = '#b060e0';
const PURP_GLOW  = '#e0c8f8';

// Neutral (Re(V) ≈ 0)
const NEUTRAL    = '#b0b0b8';

// Source markers
const SOURCE_COLOR    = '#f0c850';
const COSOURCE_COLOR  = '#b060e0';
const MARKER_RADIUS   = 4;

// Astral frame
const ASTRAL_BORDER   = 'rgba(200, 168, 74, 0.7)';
const ASTRAL_GLOW     = 'rgba(255, 244, 208, 0.3)';
const ASTRAL_FILL     = 'rgba(255, 248, 220, 0.06)';
const ASTRAL_CORNER_R = 4;

// Flow dash pattern
const DASH_LEN   = 8;
const GAP_LEN    = 12;
const FLOW_WIDTH = 1.8;

const TAU = Math.PI * 2;

// ── Potential → colour mapping ───────────────────────────────────────────────

/**
 * Map Re(V) to a colour.  Positive → warm gold, negative → cool purple.
 * The blend ensures a smooth neutral zone around zero.
 */
function potentialColor(reV: number, peak: number): string {
  if (peak < 0.01) return NEUTRAL;
  const t = Math.max(-1, Math.min(1, reV / peak)); // -1..+1

  if (t > 0.05) {
    // Blend gold
    const a = Math.min(t / 0.3, 1);
    return lerpRgb(NEUTRAL, GOLD_CORE, a);
  } else if (t < -0.05) {
    const a = Math.min(-t / 0.3, 1);
    return lerpRgb(NEUTRAL, PURP_CORE, a);
  }
  return NEUTRAL;
}

function potentialGlow(reV: number, peak: number): string {
  if (peak < 0.01) return NEUTRAL;
  const t = Math.max(-1, Math.min(1, reV / peak));
  if (t > 0.05) return lerpRgb(NEUTRAL, GOLD_GLOW, Math.min(t / 0.3, 1));
  if (t < -0.05) return lerpRgb(NEUTRAL, PURP_GLOW, Math.min(-t / 0.3, 1));
  return NEUTRAL;
}

// ── Colour helpers ───────────────────────────────────────────────────────────

/** Parse hex colour (#rrggbb) to [r,g,b]. */
function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

/** Lerp between two hex colours and return an rgb() string. */
function lerpRgb(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `rgb(${r},${g},${bl})`;
}

// ── Geometry helper ──────────────────────────────────────────────────────────

/** Parametric position → world position on a strand. */
function strandWorldPos(
  strand: Strand,
  t: number,
): { x: number; y: number } {
  const pts = strand.points;
  if (pts.length < 2) return pts[0] ?? { x: 0, y: 0 };
  const idx = Math.max(0, Math.min(t, 0.999)) * (pts.length - 1);
  const i = Math.floor(idx);
  const frac = idx - i;
  const a = pts[i];
  const b = pts[Math.min(i + 1, pts.length - 1)];
  return {
    x: a.x + frac * (b.x - a.x),
    y: a.y + frac * (b.y - a.y),
  };
}

// ── Main render function ─────────────────────────────────────────────────────

export function drawStrandGraph(
  ctx: CanvasRenderingContext2D,
  g: StrandGraph,
  fs: FluxState,
  ox: number,
  oy: number,
  now: number,
  isRectangular?: Map<number, boolean>,
): void {
  if (g.strands.size === 0 && g.loops.length === 0) return;

  // Compute peak |V| for colour normalisation
  let peakV = 0;
  for (const [, n] of g.nodes) {
    const v = Math.abs(n.voltage.re);
    if (v > peakV) peakV = v;
  }
  // Use peakCurrent for brightness normalisation
  const peakI = fs.peakCurrent > 0.01 ? fs.peakCurrent : 1;

  ctx.save();

  // Fizzle fade
  if (fs.phase === 'fizzling') {
    const elapsed = fs.time - fs.fizzleStart;
    const fizzleFade = Math.max(0, 1 - elapsed / 800);
    ctx.globalAlpha = fizzleFade;
  }

  // 1. Strand glow (potential-colored, current-bright)
  drawStrandGlow(ctx, g, fs, ox, oy, peakV, peakI);

  // 2. Animated flow dashes
  drawFlowDashes(ctx, g, fs, ox, oy, peakV, peakI);

  // 3. Loop shimmer
  drawLoopShimmer(ctx, g, ox, oy, now, peakV, isRectangular);

  // 4. Source / co-source node markers
  drawNodeMarkers(ctx, g, fs, ox, oy);

  ctx.restore();

  // 5. Loop induction indicators
  if (g.loops.length > 0) {
    drawLoopInduction(ctx, g, ox, oy, now, peakV);
  }
}

// ── 1. Strand glow ──────────────────────────────────────────────────────────

function drawStrandGlow(
  ctx: CanvasRenderingContext2D,
  g: StrandGraph,
  fs: FluxState,
  ox: number, oy: number,
  peakV: number, peakI: number,
): void {
  for (const [, strand] of g.strands) {
    const pts = strand.points;
    if (pts.length < 2) continue;

    // Current magnitude → brightness
    const absI = Math.sqrt(strand.current.re ** 2 + strand.current.im ** 2);
    const brightness = Math.min(1, absI / peakI);

    // Also factor in phosphor for persistence
    let maxPh = 0;
    for (const ph of strand.phosphor) { if (ph > maxPh) maxPh = ph; }
    const totalBright = Math.min(1, brightness + maxPh * 0.5);
    if (totalBright < 0.01) continue;

    // Average Re(V) at the two endpoints for colour
    const fromNode = g.nodes.get(strand.fromNode);
    const toNode = g.nodes.get(strand.toNode);
    const avgReV = ((fromNode?.voltage.re ?? 0) + (toNode?.voltage.re ?? 0)) * 0.5;

    const color = potentialColor(avgReV, peakV);
    const glow = potentialGlow(avgReV, peakV);

    // Outer glow pass
    ctx.save();
    ctx.globalAlpha = totalBright * 0.7;
    ctx.strokeStyle = color;
    ctx.lineWidth = 3.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = glow;
    ctx.shadowBlur = 8 * totalBright;

    ctx.beginPath();
    ctx.moveTo(ox + pts[0].x, oy + pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(ox + pts[i].x, oy + pts[i].y);
    }
    ctx.stroke();
    ctx.restore();

    // Inner bright core
    if (totalBright > 0.25) {
      ctx.save();
      ctx.globalAlpha = totalBright * 0.4;
      ctx.strokeStyle = glow;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(ox + pts[0].x, oy + pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(ox + pts[i].x, oy + pts[i].y);
      }
      ctx.stroke();
      ctx.restore();
    }
  }
}

// ── 2. Flow dashes ──────────────────────────────────────────────────────────

function drawFlowDashes(
  ctx: CanvasRenderingContext2D,
  g: StrandGraph,
  fs: FluxState,
  ox: number, oy: number,
  peakV: number, peakI: number,
): void {
  for (const [, strand] of g.strands) {
    const absI = Math.sqrt(strand.current.re ** 2 + strand.current.im ** 2);
    if (absI < 0.01) continue;

    const brightness = Math.min(1, absI / peakI);
    const pts = strand.points;
    if (pts.length < 2) continue;

    // Flow direction: from high potential to low
    const fromNode = g.nodes.get(strand.fromNode);
    const toNode = g.nodes.get(strand.toNode);
    const fromV = fromNode?.voltage.re ?? 0;
    const toV = toNode?.voltage.re ?? 0;
    const reverse = toV > fromV; // flow goes high→low

    // Average colour
    const avgReV = (fromV + toV) * 0.5;
    const color = potentialGlow(avgReV, peakV);

    // Dash offset from flowPhase
    const totalLen = strand.arcLength;
    const pattern = DASH_LEN + GAP_LEN;
    const offset = (strand.flowPhase * totalLen) % pattern;

    ctx.save();
    ctx.globalAlpha = brightness * 0.6;
    ctx.strokeStyle = color;
    ctx.lineWidth = FLOW_WIDTH;
    ctx.lineCap = 'round';
    ctx.setLineDash([DASH_LEN, GAP_LEN]);
    ctx.lineDashOffset = reverse ? offset : -offset;

    ctx.beginPath();
    ctx.moveTo(ox + pts[0].x, oy + pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(ox + pts[i].x, oy + pts[i].y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }
}

// ── 3. Loop shimmer ──────────────────────────────────────────────────────────

function drawLoopShimmer(
  ctx: CanvasRenderingContext2D,
  g: StrandGraph,
  ox: number, oy: number,
  now: number,
  peakV: number,
  isRectMap?: Map<number, boolean>,
): void {
  for (const loop of g.loops) {
    if (loop.shimmer < 0.01) continue;

    const isRect = isRectMap?.get(loop.id as number) ?? false;

    if (isRect) {
      drawAstralFrame(ctx, g, loop, ox, oy, now);
    } else {
      drawLoopGlow(ctx, loop, ox, oy, now, peakV);
    }
  }
}

function drawLoopGlow(
  ctx: CanvasRenderingContext2D,
  loop: Loop,
  ox: number, oy: number,
  now: number,
  peakV: number,
): void {
  const cx = ox + loop.center.x;
  const cy = oy + loop.center.y;
  const r = Math.max(loop.radius, 10);

  const inductionBoost = Math.min(loop.induction * 0.15, 0.6);
  const pulse = 0.7 + 0.3 * Math.sin(now * 0.003 * (loop.winding > 0 ? 1 : -1));
  const alpha = Math.min(1, loop.shimmer + inductionBoost) * pulse;

  // Winding determines colour: CW = gold, CCW = purple
  const color = loop.winding > 0 ? GOLD_CORE : PURP_CORE;
  const dim = loop.winding > 0 ? 'rgba(240, 200, 80, 0.15)' : 'rgba(176, 96, 224, 0.15)';

  ctx.save();
  ctx.globalAlpha = alpha * 0.6;

  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  grad.addColorStop(0, color);
  grad.addColorStop(0.5, dim);
  grad.addColorStop(1, 'transparent');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawAstralFrame(
  ctx: CanvasRenderingContext2D,
  g: StrandGraph,
  loop: Loop,
  ox: number, oy: number,
  now: number,
): void {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const sid of loop.strands) {
    const s = g.strands.get(sid);
    if (!s) continue;
    for (const p of s.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }

  const x = ox + minX;
  const y = oy + minY;
  const w = maxX - minX;
  const h = maxY - minY;
  const shimmerPhase = (now * 0.002) % (Math.PI * 2);
  const alpha = loop.shimmer * (0.7 + 0.3 * Math.sin(shimmerPhase));

  ctx.save();

  ctx.globalAlpha = alpha * 0.08;
  ctx.fillStyle = ASTRAL_FILL;
  ctx.fillRect(x, y, w, h);

  ctx.globalAlpha = alpha * 0.3;
  ctx.shadowColor = ASTRAL_GLOW;
  ctx.shadowBlur = 12;
  ctx.strokeStyle = ASTRAL_BORDER;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(x, y, w, h);

  ctx.shadowBlur = 0;
  ctx.globalAlpha = alpha * 0.7;
  ctx.strokeStyle = ASTRAL_BORDER;
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);

  ctx.globalAlpha = alpha * 0.9;
  ctx.fillStyle = GOLD_CORE;
  for (const [cx, cy] of [[x, y], [x + w, y], [x, y + h], [x + w, y + h]]) {
    ctx.beginPath();
    ctx.arc(cx, cy, ASTRAL_CORNER_R, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

// ── 4. Node markers ─────────────────────────────────────────────────────────

function drawNodeMarkers(
  ctx: CanvasRenderingContext2D,
  g: StrandGraph,
  fs: FluxState,
  ox: number, oy: number,
): void {
  // Source / co-source markers
  for (const pair of fs.pairs) {
    const srcNode = g.nodes.get(pair.sourceNodeId as any);
    if (srcNode) {
      drawMarker(ctx, ox + srcNode.x, oy + srcNode.y, SOURCE_COLOR, pair.V0);
    }
    const coNode = g.nodes.get(pair.coSourceNodeId as any);
    if (coNode) {
      drawMarker(ctx, ox + coNode.x, oy + coNode.y, COSOURCE_COLOR, pair.V0);
    }
  }

  // Knot / junction markers (crossings)
  for (const [, node] of g.nodes) {
    if (node.kind === 'knot' || node.kind === 'junction') {
      const v = Math.abs(node.voltage.re);
      const color = node.voltage.re >= 0 ? GOLD_CORE : PURP_CORE;
      drawMarker(ctx, ox + node.x, oy + node.y, color, Math.max(0.3, v));
    }
  }
}

function drawMarker(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  color: string,
  intensity: number,
): void {
  const alpha = Math.min(1, intensity * 1.2);
  ctx.save();
  ctx.globalAlpha = alpha * 0.5;
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.arc(x, y, MARKER_RADIUS + 2, 0, TAU);
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.arc(x, y, MARKER_RADIUS, 0, TAU);
  ctx.fill();
  ctx.restore();
}

// ── 5. Loop induction indicator ──────────────────────────────────────────────

function drawLoopInduction(
  ctx: CanvasRenderingContext2D,
  g: StrandGraph,
  ox: number, oy: number,
  now: number,
  peakV: number,
): void {
  for (const loop of g.loops) {
    if (!loop.active && loop.induction < 0.05) continue;

    const cx = ox + loop.center.x;
    const cy = oy + loop.center.y;
    const ind = loop.induction;
    if (ind < 0.05) continue;

    const rBase = Math.min(20, 6 + ind * 2);
    const breathRate = 0.004 + ind * 0.001;
    const breathAmp = 0.15 + Math.min(ind * 0.05, 0.35);
    const breath = 1 + breathAmp * Math.sin(now * breathRate);
    const r = rBase * breath;

    const color = loop.winding > 0 ? GOLD_CORE : PURP_CORE;
    const glow = loop.winding > 0 ? GOLD_GLOW : PURP_GLOW;
    const alpha = Math.min(1, ind / 3) * 0.85;

    ctx.save();

    ctx.globalAlpha = alpha * 0.4;
    const grad = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, r * 1.5);
    grad.addColorStop(0, glow);
    grad.addColorStop(1, 'transparent');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = alpha * 0.7;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }
}
